import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import useIsMobile from "../../hooks/useIsMobile";
import { getTourSteps, TourId } from "./tourSteps";
import TourSpotlight from "./TourSpotlight";
import { getTourFlag, setTourFlagStored, TourFlag } from "./tourFlags";

// Flag persistence and the onboarding-guidance decision live in ./tourFlags — this file is only
// the live tour state (which tour, which step) provided via React context.

// Which flag each tour sets when it ends, so it never replays.
const TOUR_FLAG: Record<TourId, TourFlag> = {
  chat: "chatTourCompleted",
  group: "groupTourSeen",
  asset: "assetTourSeen",
};
export interface TourStartOptions {
  hasProcessedModel?: boolean;
}

// Why a tour ended — captured on analytics so aborted tours don't inflate completions.
export type TourEndReason = "completed" | "skipped" | "target_missing";

interface TourContextValue {
  activeTour: TourId | null;
  stepIndex: number;
  start: (tourId: TourId, options?: TourStartOptions) => void;
  next: () => void;
  end: (completed?: boolean, reason?: TourEndReason) => void;
  hasFlag: (flag: TourFlag) => boolean;
  // False until the user record (and so the per-user flag keys) is known — first-visit
  // tours must not auto-start before this, or hasFlag() falsely reports "not seen".
  flagsReady: boolean;
  // The chat page registers how a tapped coachmark suggestion fills its input (returns a cleanup).
  registerSuggestionHandler: (fn: (text: string) => void) => () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export const useTour = (): TourContextValue => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within a TourProvider");
  return ctx;
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userId = useSelector((state: any) => state.user?.permissionDetails?.id ?? null);
  const { capture } = usePostHogCapture();
  const isMobileNav = useIsMobile(640);
  const isMobileGraph = useIsMobile(768);
  const [activeTour, setActiveTour] = useState<TourId | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [startOptions, setStartOptions] = useState<TourStartOptions>({});
  // Mirror of activeTour so end()'s side effects read the live value without a stale closure.
  const activeTourRef = useRef<TourId | null>(null);
  activeTourRef.current = activeTour;
  // Set by the active chat page — tapping a coachmark suggestion routes through it to fill the input.
  const suggestionHandlerRef = useRef<((text: string) => void) | null>(null);
  const isMobile = activeTour === "asset" ? isMobileGraph : isMobileNav;

  const tourCtx = { isMobile, ...startOptions };
  const steps = activeTour ? getTourSteps(activeTour, tourCtx) : [];
  const currentStep = activeTour ? steps[stepIndex] : undefined;

  const hasFlag = useCallback((flag: TourFlag) => getTourFlag(userId, flag), [userId]);

  const registerSuggestionHandler = useCallback((fn: (text: string) => void) => {
    suggestionHandlerRef.current = fn;
    return () => {
      if (suggestionHandlerRef.current === fn) suggestionHandlerRef.current = null;
    };
  }, []);

  const applySuggestion = useCallback((text: string) => suggestionHandlerRef.current?.(text), []);

  const start = useCallback(
    (tourId: TourId, options: TourStartOptions = {}) => {
      setActiveTour(tourId);
      setStepIndex(0);
      setStartOptions(options);
      capture(POSTHOG_EVENTS.TOUR_STARTED, { tour: tourId });
    },
    [capture]
  );

  const end = useCallback(
    (completed = true, reason: TourEndReason = completed ? "completed" : "skipped") => {
      const current = activeTourRef.current;
      if (!current) return; // already ended — avoids double flag/analytics writes
      setTourFlagStored(userId, TOUR_FLAG[current]);
      capture(completed ? POSTHOG_EVENTS.TOUR_COMPLETED : POSTHOG_EVENTS.TOUR_SKIPPED, {
        tour: current,
        reason,
      });
      activeTourRef.current = null;
      setActiveTour(null);
      setStepIndex(0);
    },
    [userId, capture]
  );

  const next = useCallback(() => {
    if (!activeTour) return;
    if (stepIndex >= getTourSteps(activeTour, { isMobile, ...startOptions }).length - 1) {
      end(true);
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [activeTour, stepIndex, isMobile, startOptions, end]);

  // Per-step analytics.
  useEffect(() => {
    if (activeTour && currentStep) {
      capture(POSTHOG_EVENTS.TOUR_STEP_VIEWED, {
        tour: activeTour,
        step_index: stepIndex,
        step_target: currentStep.target,
      });
    }
  }, [activeTour, stepIndex, currentStep, capture]);

  const flagsReady = Boolean(userId);

  const value = useMemo(
    () => ({ activeTour, stepIndex, start, next, end, hasFlag, flagsReady, registerSuggestionHandler }),
    [activeTour, stepIndex, start, next, end, hasFlag, flagsReady, registerSuggestionHandler]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && currentStep && (
        <TourSpotlight
          step={currentStep}
          stepIndex={stepIndex}
          stepCount={steps.length}
          onNext={next}
          onSkip={() => end(false)}
          onEnd={end}
          onSuggestion={applySuggestion}
        />
      )}
    </TourContext.Provider>
  );
};

export default TourProvider;
